import EventEmitter from '../../helpers/EventEmmiter';
import { resolveLink } from '../utils/scriptUtils';
import { midYaw } from '../utils/mathUtils';

/**
 * Runs the museum scripted tour independently from the AI agent.
 * The agent only activates when the visitor asks a question.
 *
 * Events:
 *   'paused'          — user spoke mid-playback; guide is holding position
 *   'resumed'         — agent finished answering; guide continues
 *   'point:start'     — entered a new point, payload: { sceneIdx, pointIdx }
 *   'scene:completed' — all points of a guide scene are done
 *   'completed'       — entire tour finished
 *   'stopped'         — explicitly stopped (e.g. on disconnect)
 */
class MuseumGuideService extends EventEmitter {
  constructor({ guide, defaultLang, bridge, audioPlayer, onSceneEnd }) {
    super();
    this._guide = [...guide].sort((a, b) => (a.position || 0) - (b.position || 0));
    this._defaultLang = defaultLang || 'en';
    this._bridge = bridge;
    this._audioPlayer = audioPlayer;
    this._onSceneEnd = onSceneEnd || null;

    this._sceneIdx = 0;
    this._pointIdx = 0;
    this._running = false;
    this._paused = false;
    this._complete = false;
    this._language = 'en';
    this._sceneEndResolve = null;
    this._sceneEndPromise = null;
    this._runGen = 0;
    this._seekMode = false;
  }

  get isRunning() { return this._running && !this._paused; }
  get isPaused()  { return this._running && this._paused; }
  get isComplete() { return this._complete; }

  getPosition() {
    return { sceneIdx: this._sceneIdx, pointIdx: this._pointIdx };
  }

  getGuide() {
    return this._guide;
  }

  seek(sceneIdx, pointIdx) {
    const language = this._language;
    this._bridge.cancelNavigate();
    this._seekMode = true;
    this.stop();
    setTimeout(() => {
      this.start(language, { sceneIdx, pointIdx });
    }, 100);
  }

  start(language = 'en', position = null) {
    if (this._running) return;
    this._language = language;
    this._sceneIdx = position?.sceneIdx || 0;
    this._pointIdx = position?.pointIdx || 0;
    this._running = true;
    this._paused = false;
    this._complete = false;
    this._runGen++;
    this._run(this._runGen);
  }

  pause() {
    if (!this._running || this._paused) return;
    this._paused = true;
    if (this._audioPlayer.isPlaying) this._audioPlayer.interrupt();
    this._fireEvent('paused');
  }

  resume(language) {
    if (!this._running || !this._paused) return;
    if (language) this._language = language;
    this._paused = false;
    this._fireEvent('resumed');
    // If _sceneEndPromise is pending the original _run() coroutine is still alive
    // (suspended at await _waitForSceneEnd). Don't spawn a second one — it will
    // continue on its own once the Promise resolves (i.e. user clicks Next).
    if (!this._sceneEndPromise) {
      this._run(this._runGen);
    }
  }

  // While the visitor's words are still being transcribed, the tour must not
  // START the next point or scene: the transcript decides whether it was a
  // question (→ pause) or noise (→ carry on). The audio already playing keeps
  // going — it may be nothing. Without this, a question asked as a point's audio
  // ended let the guide rotate to the next point and start its audio before the
  // transcript arrived and paused it. Auto-releases after `ms` as a safety net.
  holdAdvance(ms = 8000) {
    clearTimeout(this._holdTimer);
    if (!this._holdPromise) this._holdPromise = new Promise(r => { this._holdResolve = r; });
    this._holdTimer = setTimeout(() => this.releaseAdvance(), ms);
  }

  releaseAdvance() {
    clearTimeout(this._holdTimer);
    const resolve = this._holdResolve;
    this._holdPromise = null;
    this._holdResolve = null;
    if (resolve) resolve();
  }

  async _waitAdvance() {
    while (this._holdPromise) await this._holdPromise;
  }

  stop() {
    this.releaseAdvance();
    this._running = false;
    this._paused = false;
    this._audioPlayer.stop();
    if (this._sceneEndResolve) {
      this._sceneEndResolve(); // done() clears both _sceneEndResolve and _sceneEndPromise
    }
    this._fireEvent('stopped');
  }

  setLanguage(lang) {
    this._language = lang;
  }

  _coordToPosition(coord) {
    if (!coord) return null;
    let yaw = null, pitch = 0;
    if (coord.start !== undefined) {
      yaw = midYaw(coord.start, coord.end);
    } else if (coord.yaw !== undefined) {
      yaw = coord.yaw;
      pitch = coord.pitch || 0;
    }
    return yaw !== null ? { yaw, pitch } : null;
  }

  async _run(gen) {
    const stale = () => !this._running || this._paused || this._runGen !== gen;

    // Consume seek mode for this run only (reverts to normal for subsequent scenes)
    const seekMode = this._seekMode;
    this._seekMode = false;
    // seekPending is true only for the first (seeked-to) point; subsequent points rotate normally
    let seekPending = seekMode;

    while (this._sceneIdx < this._guide.length) {
      await this._waitAdvance();
      if (stale()) return;

      const scene = this._guide[this._sceneIdx];
      const points = [...(scene.points || [])].sort((a, b) => (a.position || 0) - (b.position || 0));

      // In seek mode: pass the target point's coord to navigateTo so Pannellum
      // loads the scene already positioned at the right angle (no rotation needed).
      // In normal mode: only pass coord for point 0 (initial scene entry).
      const targetCoord = seekMode
        ? this._coordToPosition(points[this._pointIdx]?.coord ?? null)
        : (this._pointIdx === 0 ? this._coordToPosition(points[0]?.coord ?? null) : null);
      const navigatedWithPosition = !!targetCoord;

      const alreadyInScene = String(this._bridge.state.currentScene) === String(scene.id);
      try {
        await this._bridge.navigateTo(scene.id, targetCoord || undefined);
      } catch (e) {
        console.warn('[guide-svc] navigateTo failed:', e.message);
      }
      if (stale()) return;

      // Seek to same scene: navigateTo resolved immediately without applying
      // the camera position, so snap it now.
      if (seekMode && alreadyInScene && targetCoord) {
        this._bridge.snapTo(targetCoord);
      }

      while (this._pointIdx < points.length) {
        await this._waitAdvance();
        if (stale()) return;

        const point = points[this._pointIdx];
        this._fireEvent('point:start', { sceneIdx: this._sceneIdx, pointIdx: this._pointIdx });
        // Skip rotation for the seeked-to point (camera already positioned).
        // Subsequent points in the same scene rotate normally.
        // In normal mode skip only for point 0 when navigateTo set the position.
        const skipRotate = seekPending ? true : (navigatedWithPosition && this._pointIdx === 0);
        seekPending = false;

        if (!skipRotate && point.coord != null) {
          const pos = this._coordToPosition(point.coord);
          if (pos) {
            try {
              await this._bridge.rotate({ ...pos, duration: 1500 });
            } catch (e) {}
            if (stale()) return;
          }
        }

        if (this._pointIdx === 0) {
          await new Promise(r => setTimeout(r, 1000));
          if (stale()) return;
        }

        if (point.renderZoneID) {
          try {
            await this._bridge.playRenderZone(point.renderZoneID, point.renderZoneType);
          } catch (e) { console.error('[museum] playRenderZone error:', e); }
          if (stale()) return;
          if (point.renderZoneType === 'image') {
            const link = resolveLink(point.link, this._language, this._defaultLang);
            if (link) {
              const outcome = await this._playAudio(link);
              this._pointIdx++;
              if (stale() || outcome === 'interrupted') return;
            } else {
              this._pointIdx++;
            }
          } else {
            this._pointIdx++;
          }
        } else if (point.hotspotID) {
          const waitForClose = point.hotspotType !== 'text';
          try {
            await this._bridge.openHotspot(point.hotspotID, waitForClose);
          } catch (e) {}
          if (stale()) return;
          if (point.hotspotType === 'text') {
            const link = resolveLink(point.link, this._language, this._defaultLang);
            if (link) {
              const outcome = await this._playAudio(link);
              this._bridge.closeHotspot();
              this._pointIdx++;
              if (stale() || outcome === 'interrupted') return;
            } else {
              this._bridge.closeHotspot();
              this._pointIdx++;
            }
          } else {
            this._pointIdx++;
          }
        } else {
          const link = resolveLink(point.link, this._language, this._defaultLang);
          if (link) {
            const outcome = await this._playAudio(link);
            this._pointIdx++;
            if (stale() || outcome === 'interrupted') return;
          } else {
            this._pointIdx++;
          }
        }
      }

      const hasNextScene = this._sceneIdx < this._guide.length - 1;
      const showPopup = hasNextScene && scene.scene_end_popup !== false;
      const lastPoint = points[points.length - 1];
      const lastCoord = lastPoint?.coord ? this._coordToPosition(lastPoint.coord) : null;
      await this._waitForSceneEnd(showPopup, lastCoord);
      if (stale()) return;

      this._pointIdx = 0;
      this._sceneIdx++;
      this._fireEvent('scene:completed', { scene });
    }

    this._running = false;
    this._complete = true;
    this._fireEvent('completed');
  }

  _waitForSceneEnd(showPopup = true, lastCoord = null) {
    // Last scene — no next room to navigate to, skip the popup entirely.
    if (!showPopup || !this._onSceneEnd) {
      return new Promise(r => setTimeout(r, 1500));
    }

    // Return the same Promise if already waiting (e.g. guide resumed after a
    // user question while the mini "Next" button was visible). This prevents
    // a second popup from being created on each resume() call.
    if (this._sceneEndPromise) return this._sceneEndPromise;

    this._sceneEndPromise = new Promise(resolve => {
      const done = () => {
        this._paused = false; // clear pause so _run() can continue if agent was speaking
        this._sceneEndResolve = null;
        this._sceneEndPromise = null;
        resolve();
      };
      this._sceneEndResolve = done;
      this._onSceneEnd(done, lastCoord);
    });
    return this._sceneEndPromise;
  }

  _playAudio(url) {
    return new Promise((resolve) => {
      const onEnded       = () => { cleanup(); resolve('ended'); };
      const onInterrupted = () => { cleanup(); resolve('interrupted'); };
      const onError       = () => { cleanup(); resolve('error'); };

      const cleanup = () => {
        this._audioPlayer.off('ended', onEnded);
        this._audioPlayer.off('interrupted', onInterrupted);
        this._audioPlayer.off('error', onError);
      };

      this._audioPlayer.on('ended', onEnded);
      this._audioPlayer.on('interrupted', onInterrupted);
      this._audioPlayer.on('error', onError);

      this._audioPlayer.play(url);
    });
  }
}

export default MuseumGuideService;
